import { createQuickCheckInSubmission, runQuickCheckInPrimaryAction } from '../utils/quickCheckInSubmission';
import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, addDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { uploadIncidentMedia } from '../components/Dashboard/Incidents/Media/mediaUploadService';
import { isDebugFlagEnabled } from '../utils/debugTools';
import { saveDirectQuickNote } from '../utils/quickLogDirectSave';
import {
  QUICK_LOG_SMART_CAPTURE_ACTIONS,
  runQuickLogSmartCaptureFlow,
} from '../utils/quickLogSmartCaptureFlow';
import { runQuickCheckInCaptureFirstFlow } from '../utils/quickCheckInCaptureFirstAdapter';
import { saveQuickCheckInSourceNote } from '../utils/quickCheckInSourceNote';
import { saveQuickLogPhotoSource } from '../utils/quickLogPhotoSourceSave';
import { IMAGE_CAPTURE_INTENTS } from '../features/imageCapture/imageCaptureIntent';
import { applyImageDisplayName } from '../features/imageCapture/imageDisplayName';
import {
  buildCaptureBundle,
  buildCaptureBundleSourceText,
  partitionCaptureAttachments,
} from '../features/captureBundle/captureBundle';
import { saveImportedLogs } from '../components/Dashboard/ImportLogs/importLogsService';
import {
  captureCareSourceForProcessing,
} from '../features/careSourceProcessing/careSourceProcessingClient';
import { buildQuickLogCareSourceDraftPayload, saveQuickLogCareSourceDraft } from '../features/careSourceProcessing/saveQuickLogCareSourceDraft';
import { syncDoctorVisitTimelineEntry } from '../services/doctorVisitTimelineService';
import { buildQuickCheckInDoctorVisitData, hasQuickCheckInDoctorVisitSignal } from '../utils/quickCheckInDoctorVisit';
import { trackEvent, trackEventOnce } from '../services/analytics';

const extractQuickLogEntriesCallable = httpsCallable(functions, 'extractQuickLogEntries');

const createTimelineRefreshEvent = (eventName, detail) => (
  typeof CustomEvent !== 'undefined'
    ? new CustomEvent(eventName, { detail })
    : { type: eventName, detail }
);

const createPhotoCaptureRequestId = () => (
  `photo_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

const buildSaveFlowDeps = (deps = {}) => ({
  db,
  collection,
  addDoc,
  doc,
  serverTimestamp,
  updateDoc,
  uploadIncidentMedia,
  saveDirectQuickNote,
  runQuickLogSmartCaptureFlow,
  runQuickCheckInCaptureFirstFlow,
  saveQuickCheckInSourceNote,
  saveQuickLogPhotoSource,
  extractQuickLogEntries: extractQuickLogEntriesCallable,
  captureCareSourceForProcessing,
  saveQuickLogCareSourceDraft,
  saveImportedLogs,
  syncDoctorVisitTimelineEntry,
  buildQuickCheckInDoctorVisitData,
  hasQuickCheckInDoctorVisitSignal,
  dispatchEvent: (event) => window.dispatchEvent(event),
  createCustomEvent: createTimelineRefreshEvent,
  ...deps,
});

const useQuickCheckInSaveFlow = ({
  child,
  onComplete: onCaptureComplete,
  onConvertToEntries,
  isEditMode,
  entryId,
  initialData,
  noteData,
  setNoteData,
  selectedDate,
  selectedTime,
  importantMoment,
  user,
  photoFile,
  photoIntent = '',
  removePhoto,
  combinedTags,
  quickCaptureOriginalText,
  quickCaptureNormalizedText,
  quickCaptureTranscript,
  quickCaptureStructureDecision,
  quickNoteCategoryDecision,
  quickLogRoutingDecision,
  quickLogRoutingPreview,
  resolvedCategory,
  detectedQuickNoteEntities,
  isQuickLogRoutingDebugMode,
  isQuickNoteCategoryAuditMode,
  noteText,
  hasNoteText,
  hasAttachedMedia,
  headless,
  autoSubmitOnOpen,
  open,
  deps,
}) => {
  const submissionRef = useRef(null);
  if (!submissionRef.current) submissionRef.current = createQuickCheckInSubmission();
  const onComplete = useCallback((result) => {
    submissionRef.current.complete();
    onCaptureComplete?.(result);
  }, [onCaptureComplete]);
  useEffect(() => {
    if (!open) submissionRef.current.complete();
  }, [open]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [quickLogDebugPayload, setQuickLogDebugPayload] = useState(null);
  const autoSubmitAttemptedRef = useRef(false);
  const primaryActionRef = useRef(null);
  const photoCaptureRequestRef = useRef({ file: null, id: '' });
  const shouldSaveInstantly = isEditMode;
  const shouldSaveWithCaptureFirst = !isEditMode;

  const logQuickNoteCategoryAudit = useCallback((context, extra = {}) => {
    if (!isQuickNoteCategoryAuditMode) {
      return null;
    }

    const payload = {
      context,
      textPreview: noteText.slice(0, 180),
      categoryDecision: quickNoteCategoryDecision,
      transcriptNormalization: quickCaptureTranscript,
      structureDecision: quickCaptureStructureDecision,
      ...extra,
    };

    // eslint-disable-next-line no-console
    console.debug('[QuickCheckIn] quick-note-category-audit', payload);
    setQuickLogDebugPayload((prev) => ({
      ...(prev || {}),
      quickNoteCategoryAudit: payload,
    }));

    return payload;
  }, [
    isQuickNoteCategoryAuditMode,
    noteText,
    quickCaptureStructureDecision,
    quickCaptureTranscript,
    quickNoteCategoryDecision,
  ]);

  const saveQuickNote = useCallback(async ({ toastMessage = '' } = {}) => {
    const text = quickCaptureOriginalText.trim();
    const hasContent = Boolean(text || photoFile || noteData?.audioBlob);
    if (!hasContent) return;

    const saveFlowDeps = buildSaveFlowDeps(deps);

    logQuickNoteCategoryAudit('saveQuickNote');
    setSaveError('');
    setSaving(true);
    try {
      const saveResult = await saveFlowDeps.saveDirectQuickNote({
        child,
        text,
        selectedDate,
        selectedTime,
        resolvedCategory,
        importantMoment,
        user,
        noteData,
        tags: combinedTags,
        transcriptNormalization: quickCaptureTranscript,
        isEditMode,
        entryId,
        initialData,
        photoFile,
        photoIntent,
        audioBlob: noteData?.audioBlob,
        deps: {
          db: saveFlowDeps.db,
          collection: saveFlowDeps.collection,
          addDoc: saveFlowDeps.addDoc,
          doc: saveFlowDeps.doc,
          serverTimestamp: saveFlowDeps.serverTimestamp,
          updateDoc: saveFlowDeps.updateDoc,
          uploadIncidentMedia: saveFlowDeps.uploadIncidentMedia,
          dispatchEvent: saveFlowDeps.dispatchEvent,
          createCustomEvent: saveFlowDeps.createCustomEvent,
        },
      });

      trackEvent('quick_log_saved', {
        save_mode: isEditMode ? 'edit' : 'direct',
      });
      trackEventOnce('first_log_saved', 'first_log_saved', {
        source: 'quick_log',
      });

      onComplete?.(toastMessage
        ? {
            ...(saveResult?.completionPayload || {}),
            toastMessage,
          }
        : saveResult?.completionPayload);
      setNoteData({ text: '', mediaFile: null, audioBlob: null });
      removePhoto();
    } catch (error) {
      console.error('Error saving quick note:', error);
      setSaveError('We could not save this note. Check your connection and try again.');
      trackEvent('quick_log_failed', {
        save_mode: isEditMode ? 'edit' : 'direct',
        error_code: error?.code || 'unknown',
      });
    } finally {
      setSaving(false);
    }
  }, [
    child,
    combinedTags,
    deps,
    entryId,
    importantMoment,
    initialData,
    isEditMode,
    logQuickNoteCategoryAudit,
    noteData,
    onComplete,
    photoFile,
    photoIntent,
    quickCaptureOriginalText,
    quickCaptureTranscript,
    removePhoto,
    resolvedCategory,
    selectedDate,
    selectedTime,
    setNoteData,
    user,
  ]);

  const savePhotoSourceNote = useCallback(async () => {
    const isPhotoSourceIntent = photoIntent === IMAGE_CAPTURE_INTENTS.READ_TEXT
      || photoIntent === IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
    if (!isPhotoSourceIntent || !photoFile || noteData?.audioBlob) {
      return false;
    }

    if (photoCaptureRequestRef.current.file !== photoFile) {
      photoCaptureRequestRef.current = {
        file: photoFile,
        id: createPhotoCaptureRequestId(),
      };
    }

    const saveFlowDeps = buildSaveFlowDeps(deps);
    logQuickNoteCategoryAudit('savePhotoSourceNote');
    setSaveError('');
    setSaving(true);
    try {
      const sourceResult = await saveFlowDeps.saveQuickLogPhotoSource({
        child,
        photoFile,
        user,
        selectedDate,
        selectedTime,
        caregiverContext: quickCaptureOriginalText.trim(),
        photoIntent,
        clientRequestId: photoCaptureRequestRef.current.id,
        uploadIncidentMedia: saveFlowDeps.uploadIncidentMedia,
        captureCareSourceForProcessing: saveFlowDeps.captureCareSourceForProcessing,
      });

      const caregiverContext = quickCaptureOriginalText.trim();

      onComplete?.({
        category: 'log',
        text: '',
        structuredEntries: [],
        captureSource: sourceResult || null,
        savedCount: 0,
        toastMessage: photoIntent === IMAGE_CAPTURE_INTENTS.SAVE_PHOTO
          ? (caregiverContext ? 'Photo and note saved.' : 'Photo saved.')
          : 'Photo saved. We will add text reading next.',
      });
      setNoteData({ text: '', mediaFile: null, audioBlob: null });
      removePhoto();
      photoCaptureRequestRef.current = { file: null, id: '' };
      trackEvent('quick_log_saved', {
        save_mode: photoIntent === IMAGE_CAPTURE_INTENTS.SAVE_PHOTO
          ? 'photo_source_saved'
          : 'photo_source_pending_ocr',
      });
      trackEventOnce('first_log_saved', 'first_log_saved', {
        source: 'quick_log',
      });
      return true;
    } catch (error) {
      console.error('Error saving photo source:', error);
      setSaveError(error?.photoSaveStage === 'upload'
        ? 'We could not upload this photo. Check your connection and try again.'
        : 'The photo uploaded, but Lifelog could not save it yet. Your photo and note are still here. Try again.');
      trackEvent('quick_log_failed', {
        save_mode: photoIntent === IMAGE_CAPTURE_INTENTS.SAVE_PHOTO
          ? 'photo_source_saved'
          : 'photo_source_pending_ocr',
        error_code: error?.code || 'unknown',
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    child,
    deps,
    logQuickNoteCategoryAudit,
    noteData?.audioBlob,
    onComplete,
    photoFile,
    photoIntent,
    quickCaptureOriginalText,
    removePhoto,
    selectedDate,
    selectedTime,
    setNoteData,
    user,
  ]);

  const saveVoiceSourceNote = useCallback(async () => {
    const captureBundle = buildCaptureBundle({
      childId: child?.id || '',
      selectedDate,
      selectedTime,
      text: quickCaptureOriginalText.trim(),
      photoFile,
      audioBlob: noteData?.audioBlob,
      photoIntent,
    });
    const audioBlob = captureBundle.audioBlob;
    const caregiverText = quickCaptureOriginalText.trim();
    const voiceTranscript = String(noteData?.voiceTranscript || '').trim();
    const text = buildCaptureBundleSourceText({ caregiverText, voiceTranscript });
    if (!audioBlob) return false;

    const saveFlowDeps = buildSaveFlowDeps(deps);
    setSaveError('');
    setSaving(true);
    try {
      const clientRequestId = submissionRef.current.getRequestId({
        childId: child?.id, userId: user?.uid, text, audioBlob,
        date: selectedDate instanceof Date ? selectedDate.toISOString() : selectedDate,
        selectedTime, importantMoment, photoFile,
      });
      const uploadedPhoto = captureBundle.photoFile
        ? await saveFlowDeps.uploadIncidentMedia(
          { file: captureBundle.photoFile, type: 'image' },
          null,
          `photo-${clientRequestId}`,
          `careSources/images/${child?.id || 'unknown'}/${clientRequestId}`
        )
        : [];
      const uploadedAudio = await saveFlowDeps.uploadIncidentMedia(
        null,
        audioBlob,
        `voice-${clientRequestId}`,
        `careSources/voice/${child?.id || 'unknown'}/${clientRequestId}`
      );
      const uploaded = [...uploadedPhoto, ...uploadedAudio];
      const uploadedAttachments = partitionCaptureAttachments(uploaded);
      const imageAttachments = applyImageDisplayName({
        attachments: uploadedAttachments.imageAttachments,
        date: selectedDate,
        time: selectedTime,
        originalFileName: photoFile?.name || '',
      });
      const audioAttachments = uploadedAttachments.audioAttachments.map((attachment) => ({
        ...attachment,
        mimeType: noteData?.audioMimeType || audioBlob.type || 'audio/webm',
        fileName: noteData?.audioFileName || attachment.filename || 'Voice note',
        originalFileName: noteData?.audioOriginalFileName || attachment.filename || '',
      }));
      if (!audioAttachments.some((attachment) => attachment?.url)) {
        throw Object.assign(new Error('The recording could not be uploaded. Please try again.'), { code: 'audio-upload-missing' });
      }
      const { payload: voiceSourcePayload } = buildQuickLogCareSourceDraftPayload({
        child,
        user,
        text,
        sourceText: text,
        normalizedText: quickCaptureNormalizedText,
        selectedDate,
        selectedTime,
        importantMoment,
        sourceType: 'dictated_text',
        sourceMode: 'voice_capture',
        inputMode: 'voice',
        imageAttachments,
        audioAttachments,
        metadata: {
          transcription: noteData?.transcription || {},
          caregiverText,
          voiceTranscript,
          originalAudioPreserved: true,
        },
      });
      const hasTranscript = Boolean(voiceTranscript);
      const draftResult = await saveFlowDeps.captureCareSourceForProcessing({
        childId: child?.id || '',
        rawText: text,
        sourceType: 'dictated_text',
        clientRequestId,
        sourceMetadata: {
          ...voiceSourcePayload.sourceMetadata,
          ...(imageAttachments.length > 0 && { imageAttachments }),
          caregiverText,
          voiceTranscript,
          textExtractionStatus: hasTranscript ? 'extracted' : 'pending',
          transcriptionStatus: hasTranscript ? 'completed' : 'pending',
          transcription: hasTranscript
            ? (noteData?.transcription || { status: 'completed' })
            : { status: 'pending' },
          originalAudioPreserved: true,
        },
      });
      if (!draftResult?.ok) throw new Error('Voice source was not saved.');

      const captureSource = draftResult.sourceNoteId ? {
        sourceNoteId: draftResult.sourceNoteId,
        sourceNotePath: `careSources/${draftResult.sourceNoteId}`,
        sourceNoteCollection: 'careSources',
        sourceNoteType: 'care_source',
      } : null;

      onComplete?.({
        category: 'log',
        text,
        captureSource,
        savedCount: 0,
        toastMessage: 'Capture saved. You can move on while Lifelog organizes it.',
      });
      setNoteData?.({ text: '', mediaFile: null, audioBlob: null, voiceTranscript: '' });
      removePhoto?.();
      trackEvent('quick_log_saved', { save_mode: 'voice_capture_source' });
      trackEventOnce('first_log_saved', 'first_log_saved', { source: 'voice' });
      return true;
    } catch (error) {
      console.error('Error saving voice source:', error);
      setSaveError(error?.code === 'audio-upload-missing'
        ? error.message
        : 'We could not save this capture. Check your connection and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [child, deps, importantMoment, noteData, onComplete, photoFile, photoIntent, quickCaptureNormalizedText, quickCaptureOriginalText, removePhoto, selectedDate, selectedTime, setNoteData, user]);

  const handleSmartSaveOrCapture = useCallback(async () => {
    const text = quickCaptureOriginalText.trim();
    const normalizedText = quickCaptureNormalizedText.trim();
    if (!text) {
      return;
    }

    const clientRequestId = submissionRef.current.getRequestId({
      childId: child?.id, userId: user?.uid, text,
      date: selectedDate instanceof Date ? selectedDate.toISOString() : selectedDate,
      selectedTime, importantMoment, photoFile,
    });
    const routingDecision = {
      ...quickLogRoutingDecision,
      baseMode: quickLogRoutingDecision.baseMode || quickLogRoutingDecision.mode,
      finalMode: quickLogRoutingDecision.finalMode || quickLogRoutingDecision.mode,
      shouldUseBackendExtraction: Boolean(quickLogRoutingDecision.shouldUseBackendExtraction),
      shouldReviewInApp: Boolean(quickLogRoutingDecision.shouldReviewInApp),
      shouldSaveLocally: Boolean(quickLogRoutingDecision.shouldSaveLocally),
      routingPreview: {
        ...(quickLogRoutingDecision.routingPreview || {}),
        route: quickLogRoutingDecision.routingPreview?.route || (quickLogRoutingDecision.shouldUseBackendExtraction ? 'claude' : 'review_needed'),
        reason: quickLogRoutingDecision.routingPreview?.reason || quickLogRoutingDecision.reason,
        shouldUseBackendExtraction: Boolean(quickLogRoutingDecision.shouldUseBackendExtraction),
        baseMode: quickLogRoutingDecision.baseMode || quickLogRoutingDecision.mode,
        finalMode: quickLogRoutingDecision.finalMode || quickLogRoutingDecision.mode,
      },
    };
    const saveFlowDeps = buildSaveFlowDeps(deps);

    if (isDebugFlagEnabled('debugImportParser')) {
      // eslint-disable-next-line no-console
      console.debug('[ImportParser] quick-note-capture-click', {
        childId: child?.id || null,
        importantMoment,
        resolvedCategory,
        routingMode: routingDecision.mode,
        routingReason: routingDecision.reason,
        routingPreview: quickLogRoutingPreview,
        preview: text.slice(0, 180),
        transcriptNormalization: quickCaptureTranscript,
        structureDecision: quickCaptureStructureDecision,
      });
    }

    try {
      setSaveError('');
      setSaving(true);

      logQuickNoteCategoryAudit('handleSmartSaveOrCapture', {
        routingMode: routingDecision.mode,
        routingReason: routingDecision.reason,
      });

      let capturedImageAttachments = [];
      if (photoFile) {
        try {
          const uploaded = await saveFlowDeps.uploadIncidentMedia(
            { file: photoFile, type: 'image' },
            null,
            `quick-log-${clientRequestId}`,
            `careSources/quickLogPhotos/${child?.id || 'unknown'}/${clientRequestId}`,
            { imageObjectName: clientRequestId }
          );
          capturedImageAttachments = applyImageDisplayName({
            attachments: Array.isArray(uploaded) ? uploaded : [],
            date: selectedDate,
            time: selectedTime,
            originalFileName: photoFile.name,
          });
        } catch (uploadError) {
          console.warn('[QuickLog] Photo upload failed; continuing without photo.', uploadError);
        }
      }

      let draftResult = null;
      try {
        draftResult = await saveFlowDeps.saveQuickLogCareSourceDraft({
          clientRequestId,
          child,
          user,
          text,
          sourceText: quickCaptureOriginalText,
          normalizedText,
          selectedDate,
          selectedTime,
          importantMoment,
          routingDecision,
          routingPreview: quickLogRoutingPreview,
          transcriptNormalization: quickCaptureTranscript,
          structureDecision: quickCaptureStructureDecision,
          quickNoteCategoryDecision,
          imageAttachments: capturedImageAttachments,
          deps: {
            captureCareSourceForProcessing: saveFlowDeps.captureCareSourceForProcessing,
            db: saveFlowDeps.db,
            collection: saveFlowDeps.collection,
            addDoc: saveFlowDeps.addDoc,
            serverTimestamp: saveFlowDeps.serverTimestamp,
          },
        });

        if (!draftResult?.ok || !draftResult?.sourceNoteId) {
          throw new Error('The capture was not acknowledged.');
        }

        if (draftResult?.ok && draftResult?.sourceNoteId) {
          const hasStructuredReviewHandler = typeof onConvertToEntries === 'function';
          const shouldContinueStructuredFlow = quickLogRoutingDecision.mode !== 'simple_local' && hasStructuredReviewHandler;

          if (quickLogRoutingDecision.mode !== 'simple_local' && !hasStructuredReviewHandler && hasQuickCheckInDoctorVisitSignal(text)) {
            const doctorVisitData = buildQuickCheckInDoctorVisitData({
              text,
              selectedDate,
              selectedTime,
            });

            if (doctorVisitData) {
              try {
                await saveFlowDeps.syncDoctorVisitTimelineEntry?.(
                  `${draftResult.sourceNoteId}-doctor-visit`,
                  doctorVisitData
                );
              } catch (doctorVisitError) {
                console.warn('Quick note doctor visit save failed; continuing with source review flow.', doctorVisitError);
              }
            }
          }

          if (!shouldContinueStructuredFlow) {
            onComplete?.({
              category: 'log',
              text,
              structuredEntries: [],
              captureSource: draftResult.captureSource || null,
              savedCount: 0,
              toastMessage: 'Saved. Review it when you are ready.',
            });
            setNoteData?.({ text: '', mediaFile: null, audioBlob: null });
            removePhoto?.();
            trackEvent('quick_log_saved', {
              save_mode: 'quick_log_care_source_draft',
            });
            trackEventOnce('first_log_saved', 'first_log_saved', {
              source: 'quick_log',
            });
            return;
          }
        }
      } catch (draftError) {
        // A lost response may follow a successful save. Retry this request ID instead
        // of creating another source through a different fallback path.
        throw Object.assign(draftError, { captureSaveFailed: true });
      }

      const smartCaptureResult = await saveFlowDeps.runQuickCheckInCaptureFirstFlow({
        runQuickLogSmartCaptureFlow: saveFlowDeps.runQuickLogSmartCaptureFlow,
        saveCaptureSourceNote: saveFlowDeps.saveQuickCheckInSourceNote,
        persistedSourceNote: draftResult?.captureSource || draftResult || null,
        text,
        sourceText: quickCaptureOriginalText,
        normalizedText,
        child,
        selectedDate,
        selectedTime,
        importantMoment,
        routingDecision,
        routingPreview: quickLogRoutingPreview,
        transcriptNormalization: quickCaptureTranscript,
        structureDecision: quickCaptureStructureDecision,
        quickNoteCategoryDecision,
        imageAttachments: capturedImageAttachments,
        detectedQuickNoteEntities,
        hasReviewHandler: typeof onConvertToEntries === 'function',
        extractQuickLogEntries: saveFlowDeps.extractQuickLogEntries,
        captureCareSourceForProcessing: saveFlowDeps.captureCareSourceForProcessing,
        user,
        deps: {
          db: saveFlowDeps.db,
          collection: saveFlowDeps.collection,
          addDoc: saveFlowDeps.addDoc,
          serverTimestamp: saveFlowDeps.serverTimestamp,
        },
      });

      if (smartCaptureResult.error) {
        console.error('Error running smart capture for quick note:', smartCaptureResult.error);
      }

      if (isQuickLogRoutingDebugMode && smartCaptureResult.debugPayload) {
        // eslint-disable-next-line no-console
        console.warn(
          smartCaptureResult.error
            ? '[ImportParser] quick-note-capture-error'
            : '[ImportParser] quick-note-capture-mode',
          smartCaptureResult.debugPayload
        );
        setQuickLogDebugPayload(smartCaptureResult.debugPayload);
      }

      if (smartCaptureResult.action === QUICK_LOG_SMART_CAPTURE_ACTIONS.REVIEW) {
        await onConvertToEntries?.(smartCaptureResult.reviewPayload);
        return;
      }

      if (smartCaptureResult.action === QUICK_LOG_SMART_CAPTURE_ACTIONS.SOURCE_PROCESSED) {
        if (smartCaptureResult.error || smartCaptureResult.ok === false) {
          setSaveError('Your media uploaded, but the note could not be saved. Please try again.');
          return;
        }

        onComplete?.({
          category: 'log',
          text,
          structuredEntries: [],
          captureSource: smartCaptureResult.captureSource || null,
          savedCount: 0,
          toastMessage: 'Saved. Review it when you are ready.',
        });
        setNoteData?.({ text: '', mediaFile: null, audioBlob: null });
        removePhoto?.();
        trackEvent('quick_log_saved', {
          save_mode: 'capture_first_source_processing',
        });
        trackEventOnce('first_log_saved', 'first_log_saved', {
          source: 'quick_log',
        });
        return;
      }

      if (smartCaptureResult.action === QUICK_LOG_SMART_CAPTURE_ACTIONS.DIRECT_SAVE) {
        await saveQuickNote({
          toastMessage: smartCaptureResult?.debugPayload?.captureFirstFallbackUsed
            ? 'Saved the note. We could not find extra details this time.'
            : '',
        });
        return;
      }
    } catch (error) {
      console.error('Error running smart capture for quick note:', error);
      if (error.captureSaveFailed) {
        setSaveError('We could not confirm this note was saved. Your note is still here. Check your connection and try again.');
        return;
      }
      if (routingDecision.shouldUseBackendExtraction) {
        setSaveError('Your media uploaded, but the note could not be saved. Please try again.');
        return;
      }
      await saveQuickNote({
        toastMessage: 'Saved the note. We could not find extra details this time.',
      });
    } finally {
      setSaving(false);
    }
  }, [
    child,
    deps,
    detectedQuickNoteEntities,
    importantMoment,
    isQuickLogRoutingDebugMode,
    logQuickNoteCategoryAudit,
    onConvertToEntries,
    onComplete,
    photoFile,
    quickCaptureNormalizedText,
    quickCaptureOriginalText,
    quickCaptureStructureDecision,
    quickCaptureTranscript,
    quickLogRoutingDecision,
    quickLogRoutingPreview,
    quickNoteCategoryDecision,
    resolvedCategory,
    saveQuickNote,
    selectedDate,
    selectedTime,
    setNoteData,
    removePhoto,
    user,
  ]);

  const handlePrimaryAction = useCallback(() => submissionRef.current.run(() => (
    runQuickCheckInPrimaryAction({
      hasNoteText, hasAttachedMedia, noteData, isEditMode, photoIntent, photoFile,
      saveVoiceSourceNote, savePhotoSourceNote, saveQuickNote, handleSmartSaveOrCapture,
    })
  )), [
    handleSmartSaveOrCapture, hasAttachedMedia, hasNoteText, isEditMode,
    noteData, photoFile, photoIntent, savePhotoSourceNote, saveVoiceSourceNote, saveQuickNote,
  ]);

  primaryActionRef.current = handlePrimaryAction;

  useEffect(() => {
    if (!headless || !autoSubmitOnOpen || !open) {
      autoSubmitAttemptedRef.current = false;
      return undefined;
    }

    if (saving || autoSubmitAttemptedRef.current || (!hasNoteText && !hasAttachedMedia)) {
      return undefined;
    }

    autoSubmitAttemptedRef.current = true;
    const timer = window.setTimeout(() => {
      primaryActionRef.current?.();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    autoSubmitOnOpen,
    hasAttachedMedia,
    hasNoteText,
    headless,
    open,
    saving,
  ]);

  return {
    saving,
    saveError,
    quickLogDebugPayload,
    shouldSaveInstantly,
    shouldSaveWithCaptureFirst,
    saveQuickNote,
    savePhotoSourceNote,
    saveVoiceSourceNote,
    handleSmartSaveOrCapture,
    handlePrimaryAction,
  };
};

export default useQuickCheckInSaveFlow;
